import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CircleCheck, CircleX, Clock, LogIn, RotateCw } from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import SEO from '../../components/SEO';
import paymentService from '../../services/paymentService';
import { getErrorMessage } from '../../utils/apiError';
import { hasSession, loginPath } from '../../utils/session';
import PaymentOutcome from './components/PaymentOutcome';
import { SupportCard } from './components/OrderSummaryCard';
import './checkout.css';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

// What the live region says for each phase.
const ANNOUNCE = new Map([
  ['checking', 'Checking your payment.'],
  ['success', 'Payment received.'],
  ['failed', 'Payment failed.'],
  ['timeout', "Payment not confirmed yet. We'll update your order once it is."],
  ['error', "We couldn't check your payment."],
  ['signin', 'Sign in to see your payment status.'],
  ['missing', 'No order to show.'],
]);

// A 4xx (other than timeouts and rate limits) won't fix itself by asking again.
const isFinalError = status => status >= 400 && status < 500 && status !== 408 && status !== 429;

/**
 * /payment-status?orderId=… — where a payment gateway (PhonePe) sends the buyer back.
 * Polls the order every 3 seconds for up to 2 minutes.
 */
const PaymentReturnPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = (searchParams.get('orderId') || '').trim();
  const signedIn = hasSession();

  const [phase, setPhase] = useState(() => {
    if (!orderId) return 'missing';
    return signedIn ? 'checking' : 'signin';
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!orderId || !signedIn) return undefined;

    let cancelled = false;
    let timer;
    let lastError = null;
    const startedAt = Date.now();

    setPhase('checking');

    const check = async () => {
      try {
        const status = await paymentService.getOrderPaymentStatus(orderId);
        if (cancelled) return;
        lastError = null;
        if (status === 'SUCCESS') {
          setPhase('success');
          return;
        }
        if (status === 'FAILED') {
          setPhase('failed');
          return;
        }
      } catch (error) {
        if (cancelled) return;
        const code = error?.response?.status;
        if (code === 401) {
          setPhase('signin');
          return;
        }
        if (isFinalError(code)) {
          setErrorMessage(getErrorMessage(error, "We couldn't check this payment."));
          setPhase('error');
          return;
        }
        lastError = error; // network or server hiccup: keep trying until the timeout
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        if (lastError) {
          setErrorMessage(getErrorMessage(lastError, "We couldn't check this payment."));
          setPhase('error');
        } else {
          setPhase('timeout');
        }
        return;
      }
      timer = setTimeout(check, POLL_INTERVAL_MS);
    };

    check();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderId, signedIn, round]);

  const checkAgain = () => setRound(n => n + 1);
  const orderPath = `/profile/order-details/${encodeURIComponent(orderId)}`;
  const viewOrder = (primary = true) => (
    <Link
      to={orderPath}
      className={`pk-btn ${primary ? 'pk-btn--primary' : 'pk-btn--secondary'} pk-btn--sm`}
    >
      View order
    </Link>
  );

  let outcome;
  switch (phase) {
    case 'missing':
      outcome = (
        <PaymentOutcome
          tone="neutral"
          icon={<AlertCircle />}
          title="No order to show"
          actions={
            <Link to="/profile" className="pk-btn pk-btn--primary pk-btn--sm">
              Your orders
            </Link>
          }
        >
          <p>This link doesn't include an order. Your orders and their payment status are on your profile.</p>
        </PaymentOutcome>
      );
      break;
    case 'signin':
      outcome = (
        <PaymentOutcome
          tone="neutral"
          icon={<LogIn />}
          title="Sign in to see your payment"
          actions={
            <Link
              to={loginPath(`/payment-status?orderId=${encodeURIComponent(orderId)}`)}
              className="pk-btn pk-btn--primary pk-btn--sm"
            >
              Sign in
            </Link>
          }
        >
          <p>Your session has ended. Sign in again to check the payment for order {orderId}.</p>
        </PaymentOutcome>
      );
      break;
    case 'success':
      outcome = (
        <PaymentOutcome
          tone="success"
          icon={<CircleCheck />}
          title="Payment received"
          actions={
            <>
              {viewOrder()}
              <Link to="/products" className="pk-btn pk-btn--secondary pk-btn--sm">
                Browse projects
              </Link>
            </>
          }
        >
          <p>Thank you. Your project's deliverables will appear on your order page.</p>
        </PaymentOutcome>
      );
      break;
    case 'failed':
      outcome = (
        <PaymentOutcome
          tone="danger"
          icon={<CircleX />}
          title="Payment failed"
          actions={
            <>
              <Link
                to={`/checkout/pay/${encodeURIComponent(orderId)}`}
                className="pk-btn pk-btn--primary pk-btn--sm"
              >
                Try again
              </Link>
              {viewOrder(false)}
            </>
          }
        >
          <p>The payment for this order didn't go through. You can try again with UPI.</p>
        </PaymentOutcome>
      );
      break;
    case 'timeout':
      outcome = (
        <PaymentOutcome
          tone="info"
          icon={<Clock />}
          title="Payment not confirmed yet"
          actions={
            <>
              {viewOrder()}
              <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={checkAgain}>
                <RotateCw aria-hidden="true" />
                Check again
              </button>
            </>
          }
        >
          <p>We'll update your order once the payment is confirmed.</p>
        </PaymentOutcome>
      );
      break;
    case 'error':
      outcome = (
        <PaymentOutcome
          tone="danger"
          icon={<AlertCircle />}
          title="We couldn't check your payment"
          actions={
            <>
              <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={checkAgain}>
                <RotateCw aria-hidden="true" />
                Retry
              </button>
              {viewOrder(false)}
            </>
          }
        >
          <p>{errorMessage}</p>
        </PaymentOutcome>
      );
      break;
    default:
      outcome = (
        <PaymentOutcome
          tone="info"
          icon={<span className="pk-spinner" />}
          title="Checking your payment"
        >
          <p>This usually takes a few seconds. Please keep this page open.</p>
        </PaymentOutcome>
      );
  }

  return (
    <main className="pk-page co-page">
      <SEO title="Payment status" noindex />
      <div className="pk-container">
        <PageHeader title="Payment status" subtitle={orderId ? `Order ${orderId}` : undefined} />
        {/* Announces each status change to screen readers. */}
        <p className="co-sr-only" role="status" aria-live="polite">
          {ANNOUNCE.get(phase)}
        </p>
        <div className="co-outcome-wrap pk-stack" aria-busy={phase === 'checking' || undefined}>
          {outcome}
          <SupportCard />
        </div>
      </div>
    </main>
  );
};

export default PaymentReturnPage;
